/**
 * <s-article-toc data-target="article-body">
 *   Builds a table of contents from the h2/h3 headings inside #data-target, gives each heading a stable id,
 *   and marks the link of the section being read with aria-current="true" while the reader scrolls.
 *   Markup inside the element:
 *     [data-toc-list]     <ol> that receives the links
 *     [data-toc-current-mirror]  optional element in the [data-toc-wrapper] that shows the current heading (mobile bar)
 *   The closest [data-toc-wrapper] is hidden when the article has fewer than 2 headings.
 *   Links inside a <details> close it after a click (mobile dropdown).
 */
class ArticleToc extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const target = document.getElementById(this.dataset.target);
    const list = this.querySelector('[data-toc-list]');
    if (!target || !list) return;

    this.headings = Array.from(target.querySelectorAll('h2, h3')).filter((h) => h.textContent.trim() !== '');
    const wrapper = this.closest('[data-toc-wrapper]') || this;
    if (this.headings.length < 2) {
      wrapper.hidden = true;
      return;
    }

    const used = new Set(Array.from(document.querySelectorAll('[id]')).map((el) => el.id));
    list.replaceChildren();
    this.links = this.headings.map((heading, index) => {
      if (!heading.id) {
        const base = this.slugify(heading.textContent) || `section-${index + 1}`;
        let id = base;
        let n = 2;
        while (used.has(id)) id = `${base}-${n++}`;
        used.add(id);
        heading.id = id;
      }
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      link.dataset.level = heading.tagName === 'H3' ? '3' : '2';
      item.append(link);
      list.append(item);
      return link;
    });

    this.current = wrapper.querySelector('[data-toc-current-mirror]');
    const details = this.closest('details');
    if (details) {
      list.addEventListener('click', (event) => {
        if (event.target.closest('a')) details.open = false;
      }, { signal: this.abort.signal });
    }

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        this.update();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal: this.abort.signal });
    window.addEventListener('resize', onScroll, { passive: true, signal: this.abort.signal });
    this.update();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  slugify(text) {
    return text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  update() {
    if (!this.links) return;
    const offset = 140;
    let active = 0;
    this.headings.forEach((heading, index) => {
      if (heading.getBoundingClientRect().top - offset <= 0) active = index;
    });
    if (active === this.activeIndex) return;
    this.activeIndex = active;
    this.links.forEach((link, index) => {
      if (index === active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    if (this.current) this.current.textContent = this.links[active].textContent;

    // Keep the active link visible when the desktop list scrolls on its own.
    const scroller = this.matches('[data-toc-scroll]') ? this : this.querySelector('[data-toc-scroll]');
    const link = this.links[active];
    if (scroller && scroller.scrollHeight > scroller.clientHeight) {
      const top = link.offsetTop - scroller.offsetTop;
      if (top < scroller.scrollTop || top > scroller.scrollTop + scroller.clientHeight - 40) {
        scroller.scrollTo({ top: Math.max(0, top - scroller.clientHeight / 3) });
      }
    }
  }
}

if (!customElements.get('s-article-toc')) customElements.define('s-article-toc', ArticleToc);
